import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Sparkles, Swords, Users2, Share2,
  Brain, Cpu, Zap, LayoutTemplate, Rocket, Trophy, Target,
  DollarSign, TrendingUp, Layers, Play, ChevronRight, X, Maximize2,
  MessageSquare, Gavel, Check, Star, FileDown
} from "lucide-react";
import Logo from "../components/Logo";

/* ============================================================
   AiMedley — Investor Pitch Deck (public route: /pitch)
   Keyboard: ← → space PgUp PgDn Home End F(fullscreen)
   14 slides, brutalist styling, no auth required
   ============================================================ */

const BRAIN_STRIP = [
  { name: "GPT-5.1",       color: "#10B981", accent: "OpenAI" },
  { name: "Claude 4.6",    color: "#D97706", accent: "Anthropic" },
  { name: "Gemini 2.5",    color: "#3B82F6", accent: "Google" },
  { name: "DeepSeek V3",   color: "#6366F1", accent: "DeepSeek" },
  { name: "Qwen 2.5",      color: "#EF4444", accent: "Alibaba" },
  { name: "Kimi K2",       color: "#EC4899", accent: "Moonshot" },
  { name: "Llama 3.3",     color: "#F59E0B", accent: "Meta" },
  { name: "Mistral Large", color: "#8B5CF6", accent: "Mistral" },
];

export const DECK_SLIDES = [
  Cover, Problem, Insight, Solution, DebateSpotlight, HowItWorks, Brains,
  ProductHighlights, Market, Competition, BusinessModel, Roadmap, TractionMetrics, Ask,
];

export default function PitchDeck() {
  const [idx, setIdx] = useState(0);
  const slides = DECK_SLIDES.map((S) => <S key={S.name} />);

  const containerRef = useRef(null);
  const total = slides.length;

  const go = useCallback((n) => setIdx((p) => Math.max(0, Math.min(total - 1, n))), [total]);
  const next = useCallback(() => go(idx + 1), [idx, go]);
  const prev = useCallback(() => go(idx - 1), [idx, go]);
  const toggleFs = useCallback(() => {
    if (!document.fullscreenElement) containerRef.current?.requestFullscreen?.().catch(() => {});
    else document.exitFullscreen?.().catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (["ArrowRight", "PageDown", " ", "Space"].includes(e.key)) { e.preventDefault(); next(); }
      else if (["ArrowLeft", "PageUp"].includes(e.key)) { e.preventDefault(); prev(); }
      else if (e.key === "Home") { e.preventDefault(); go(0); }
      else if (e.key === "End") { e.preventDefault(); go(total - 1); }
      else if (e.key === "f" || e.key === "F") { toggleFs(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, go, total, toggleFs]);

  return (
    <div ref={containerRef} className="fixed inset-0 bg-[#FDF8F5] overflow-hidden font-body text-slate-900" data-testid="pitch-deck">
      {/* Grain overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.035] z-30"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='300' height='300'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' seed='7'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-40 flex items-center justify-between px-6 py-4">
        <Link to="/" className="hover:opacity-80 transition-opacity" data-testid="deck-home-link">
          <Logo size={28} wordmark="compact" />
        </Link>
        <div className="flex items-center gap-3">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500 font-mono" data-testid="deck-counter">
            {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </div>
          <Link
            to="/pitch/pdf"
            className="p-2 rounded-lg border-2 border-slate-900 bg-white hover:bg-slate-900 hover:text-white transition-colors inline-flex items-center gap-1 text-xs font-bold"
            data-testid="deck-pdf"
            title="Export as PDF"
          >
            <FileDown size={14} strokeWidth={2.5} /> PDF
          </Link>
          <button
            onClick={toggleFs}
            className="p-2 rounded-lg border-2 border-slate-900 bg-white hover:bg-slate-900 hover:text-white transition-colors"
            data-testid="deck-fullscreen"
            title="Fullscreen (F)"
          >
            <Maximize2 size={14} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Slide */}
      <div className="absolute inset-0 pt-16 pb-16 flex items-center justify-center z-10">
        <div key={idx} className="w-full h-full slide-in overflow-y-auto">
          {slides[idx]}
        </div>
      </div>

      {/* Bottom bar: progress + arrows */}
      <div className="absolute bottom-0 left-0 right-0 z-40 flex items-center justify-between px-6 py-4">
        <button
          onClick={prev}
          disabled={idx === 0}
          className="brut-btn brut-btn-outline disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="deck-prev"
        >
          <ArrowLeft size={14} strokeWidth={2.5} /> Prev
        </button>

        <div className="flex-1 mx-4 h-1 rounded-full bg-slate-200 overflow-hidden border border-slate-900">
          <div
            className="h-full bg-slate-900 transition-all duration-500"
            style={{ width: `${((idx + 1) / total) * 100}%` }}
            data-testid="deck-progress"
          />
        </div>

        <button
          onClick={next}
          disabled={idx === total - 1}
          className="brut-btn disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="deck-next"
        >
          Next <ArrowRight size={14} strokeWidth={2.5} />
        </button>
      </div>

      <style>{`
        .slide-in { animation: slidein 0.42s cubic-bezier(.4,0,.2,1); }
        @keyframes slidein { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
        .stagger > * { opacity: 0; animation: fadeup 0.5s forwards; }
        .stagger > *:nth-child(1) { animation-delay: 0.1s; }
        .stagger > *:nth-child(2) { animation-delay: 0.2s; }
        .stagger > *:nth-child(3) { animation-delay: 0.3s; }
        .stagger > *:nth-child(4) { animation-delay: 0.4s; }
        .stagger > *:nth-child(5) { animation-delay: 0.5s; }
        .stagger > *:nth-child(6) { animation-delay: 0.6s; }
        @keyframes fadeup { to { opacity: 1; transform: none; } from { opacity: 0; transform: translateY(10px); } }
        .marquee { animation: marquee 30s linear infinite; }
        @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
      `}</style>
    </div>
  );
}

/* ============================================================
   SLIDES
   ============================================================ */

// 1 — COVER
function Cover() {
  return (
    <div className="w-full h-full flex items-center justify-center px-8" data-testid="slide-cover">
      <div className="max-w-5xl">
        <div className="flex items-center gap-2 mb-6">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-200 border-2 border-slate-900 font-heading font-bold text-xs uppercase tracking-widest">
            <Sparkles size={11} strokeWidth={3} /> Seed pitch · 2026
          </span>
        </div>
        <div className="flex items-center gap-5 mb-8">
          <Logo size={72} wordmark="none" />
          <div>
            <h1 className="font-heading text-6xl md:text-8xl font-bold leading-none tracking-tight">AiMedley</h1>
            <div className="text-xs uppercase tracking-[0.3em] text-slate-500 font-bold mt-2">
              Multi-brain agent workforce · 2026
            </div>
          </div>
        </div>
        <p className="text-2xl md:text-4xl font-heading font-bold leading-tight mb-8 max-w-4xl">
          Build a team of AI experts once.<br />
          <span className="bg-amber-200 border-2 border-slate-900 px-2 inline-block -rotate-1">Swap their brains</span>.
          Put them in the ring. Take them anywhere.
        </p>
        <p className="text-lg text-slate-600 max-w-2xl mb-8">
          The multi-LLM workbench where personas outlive vendors. 12 brains. One workforce. Zero lock-in.
        </p>
        <div className="flex items-center gap-4 text-xs uppercase tracking-widest font-bold text-slate-500 font-mono">
          <span>← →</span>
          <span>navigate</span>
          <span className="text-slate-300">·</span>
          <span>F</span>
          <span>fullscreen</span>
        </div>
      </div>
    </div>
  );
}

// 2 — PROBLEM
function Problem() {
  return (
    <div className="w-full h-full flex items-center px-8 md:px-16" data-testid="slide-problem">
      <div className="max-w-6xl mx-auto w-full">
        <SectionEyebrow>01 · Problem</SectionEyebrow>
        <h2 className="font-heading text-5xl md:text-7xl font-bold leading-none mb-10 max-w-4xl">
          You pay for four LLMs.<br />
          You get four <span className="line-through text-slate-400">strangers</span> <span className="bg-red-200 border-2 border-slate-900 px-2 inline-block rotate-1">amnesiac strangers</span>.
        </h2>

        <div className="grid md:grid-cols-3 gap-4 stagger">
          <PainCard emoji="🧠" title="Every chat starts from zero" body="You re-explain your writing style, your team, your codebase — every session. Every tool." />
          <PainCard emoji="🎡" title="Model roulette" body="Claude for writing, GPT for reasoning, Gemini for cheap? Users tab-switch 40x a day and lose the thread." />
          <PainCard emoji="🔐" title="Locked in without portability" body="Your best prompts + persona live inside one vendor's UI. Move to another? Rebuild from scratch." />
        </div>

        <div className="mt-10 flex items-center gap-3 text-sm text-slate-500 font-mono">
          <div className="font-mono text-3xl md:text-4xl font-bold text-slate-900">$60/mo</div>
          <span>× 200M knowledge workers = <b className="text-slate-900">$140B/yr</b> spent on tools that forget them.</span>
        </div>
      </div>
    </div>
  );
}

function PainCard({ emoji, title, body }) {
  return (
    <div className="brut-card p-5 bg-white">
      <div className="text-3xl mb-2">{emoji}</div>
      <div className="font-heading font-bold text-lg mb-1.5">{title}</div>
      <div className="text-sm text-slate-600 leading-relaxed">{body}</div>
    </div>
  );
}

// 3 — INSIGHT
function Insight() {
  return (
    <div className="w-full h-full flex items-center px-8 md:px-16" data-testid="slide-insight">
      <div className="max-w-6xl mx-auto w-full">
        <SectionEyebrow>02 · Insight</SectionEyebrow>
        <div className="max-w-5xl">
          <h2 className="font-heading text-4xl md:text-6xl font-bold leading-tight mb-6">
            The value should live in the <span className="underline decoration-amber-400 decoration-8 underline-offset-4">persona</span>.
            <br />
            Not the vendor.
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed mb-10 max-w-3xl">
            Users don't want another chat window. They want <b>colleagues</b> who remember them —
            with the freedom to swap the brain behind that colleague as models keep evolving.
          </p>

          <div className="grid md:grid-cols-2 gap-6 stagger">
            <div className="brut-card p-6 bg-white">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Old world</div>
              <div className="font-heading font-bold text-2xl mb-2">Model = product</div>
              <div className="text-slate-600">Every new SOTA release restarts the user's setup.</div>
            </div>
            <div className="brut-card p-6 bg-amber-50 border-slate-900">
              <div className="text-xs uppercase tracking-widest text-slate-900 font-bold mb-2">AiMedley world</div>
              <div className="font-heading font-bold text-2xl mb-2">Persona = product · Brain = swap-in</div>
              <div className="text-slate-700">The colleague stays. The engine underneath is a choice.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// 4 — SOLUTION
function Solution() {
  return (
    <div className="w-full h-full flex items-center px-8 md:px-16" data-testid="slide-solution">
      <div className="max-w-6xl mx-auto w-full">
        <SectionEyebrow>03 · Solution</SectionEyebrow>
        <h2 className="font-heading text-5xl md:text-7xl font-bold leading-none mb-4">
          Your <span className="bg-amber-200 border-2 border-slate-900 px-2 inline-block -rotate-1">Agent Workforce</span>.
        </h2>
        <p className="text-lg text-slate-600 mb-10 max-w-2xl">
          Hire persistent AI workers. Give them a brain. Team them up. Put them in a debate. Share them with the world.
        </p>

        <div className="grid md:grid-cols-4 gap-4 stagger">
          <SolutionTile icon={Users2} title="Persistent Workers"
            body="Name, mission, memory, tools — decoupled from any model." color="#3B82F6" />
          <SolutionTile icon={Brain} title="12 Swappable Brains"
            body="OpenAI, Anthropic, Google, DeepSeek, Qwen, Kimi & more." color="#10B981" />
          <SolutionTile icon={Swords} title="Debate Mode"
            body="Same prompt → N experts argue → a Judge rules. Live streamed." color="#EF4444" />
          <SolutionTile icon={Share2} title="Public Share Links"
            body="Any worker, chat, or debate becomes a URL. Zero-friction distribution." color="#D97706" />
        </div>
      </div>
    </div>
  );
}

function SolutionTile({ icon: Icon, title, body, color }) {
  return (
    <div className="brut-card p-5 bg-white h-full flex flex-col">
      <div
        className="w-11 h-11 rounded-xl border-2 border-slate-900 flex items-center justify-center mb-3"
        style={{ backgroundColor: color + "22", color }}
      >
        <Icon size={20} strokeWidth={2.5} />
      </div>
      <div className="font-heading font-bold text-lg mb-1.5">{title}</div>
      <div className="text-sm text-slate-600 leading-relaxed">{body}</div>
    </div>
  );
}

// 5 — DEBATE SPOTLIGHT
function DebateSpotlight() {
  return (
    <div className="w-full h-full flex items-center px-8 md:px-12" data-testid="slide-debate">
      <div className="max-w-6xl mx-auto w-full">
        <SectionEyebrow>04 · Flagship feature</SectionEyebrow>
        <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-10 items-center">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Swords size={22} strokeWidth={2.5} />
              <span className="text-xs uppercase tracking-widest font-bold text-slate-500">Debate Arena</span>
            </div>
            <h2 className="font-heading text-4xl md:text-6xl font-bold leading-none mb-4">
              Watch your models fight.
            </h2>
            <p className="text-lg text-slate-600 mb-5 leading-relaxed">
              Pick 2–5 workers. Assign each a stance. Choose a judge. Hit start.
              Every debate is auto-recorded and one-click shareable.
            </p>
            <ul className="space-y-2 text-slate-700">
              {["Parallel SSE streaming — see all sides simultaneously",
                "Structured judge verdict (winner + why + where each side fell short)",
                "Public share turns every debate into a viral artifact",
                "Cost per debate: ≈ $0.01"].map((t, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check size={16} className="text-emerald-600 mt-1 shrink-0" strokeWidth={3} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <DebateMockup />
        </div>
      </div>
    </div>
  );
}

function DebateMockup() {
  return (
    <div className="brut-card p-4 bg-white rotate-[0.5deg]">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Topic</div>
      <div className="font-heading font-bold text-lg mb-3 leading-tight">Should remote-first companies mandate quarterly in-person offsites?</div>

      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">Round 1</div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <MiniTurn avatar="👩‍💼" name="Nina" stance="For" color="#10B981" text="Async slays daily standups, but relationships get built in the messy edges…" />
        <MiniTurn avatar="🧑‍💻" name="Rio" stance="Against" color="#EF4444" text="Every mandated offsite is a productivity tax on introverts who ship best solo…" />
      </div>

      <div className="brut-card p-3 bg-amber-50">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">
          <Gavel size={11} strokeWidth={3} /> Verdict by Judge Marla
        </div>
        <div className="text-sm text-slate-800"><b>Winner: Nina (narrow).</b> Rio's productivity math is real, but Nina rebutted with the "trust IOU" model that ships better long-run.</div>
      </div>

      <div className="flex items-center gap-2 mt-3 text-[10px] font-mono text-slate-400">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 dot-blink" />
        Live streaming · $0.008 · 41s
      </div>
    </div>
  );
}

function MiniTurn({ avatar, name, stance, color, text }) {
  return (
    <div className="brut-card p-2.5 bg-white">
      <div className="flex items-center gap-1.5 mb-1 text-xs">
        <span>{avatar}</span>
        <span className="font-heading font-bold">{name}</span>
        <span className="px-1.5 py-0.5 rounded-full border text-[9px] font-bold uppercase tracking-widest"
              style={{ borderColor: color, color, backgroundColor: color + "1F" }}>{stance}</span>
      </div>
      <div className="text-[11px] text-slate-600 leading-snug">{text}</div>
    </div>
  );
}

// 6 — HOW IT WORKS
function HowItWorks() {
  return (
    <div className="w-full h-full flex items-center px-8 md:px-16" data-testid="slide-how">
      <div className="max-w-6xl mx-auto w-full">
        <SectionEyebrow>05 · How it works</SectionEyebrow>
        <h2 className="font-heading text-4xl md:text-6xl font-bold leading-none mb-10">
          One prompt. Many brains. One workforce.
        </h2>

        <div className="grid md:grid-cols-4 gap-3 stagger">
          <StepBox n="01" title="Hire a worker" body="Name, mission, tools, expertise — takes 30 seconds. We seed 4 for you on signup." />
          <StepBox n="02" title="Pick a brain" body="12 models across 8 providers. Or 'Auto Brain' picks per prompt shape." />
          <StepBox n="03" title="Assemble the team" body="Group workers into teams with shared instructions + memory + broadcast prompts." />
          <StepBox n="04" title="Debate. Share. Iterate." body="Put them in the ring. Snapshot the result. Post the share link." />
        </div>

        <div className="mt-10 flex items-center gap-6 flex-wrap">
          <StatChip label="First byte in" value="< 800ms" />
          <StatChip label="Debate cost" value="≈ $0.01" />
          <StatChip label="Onboarding time" value="< 30s" />
          <StatChip label="Vendor lock-in" value="0" />
        </div>
      </div>
    </div>
  );
}

function StepBox({ n, title, body }) {
  return (
    <div className="brut-card p-4 bg-white h-full">
      <div className="text-4xl font-heading font-bold text-amber-500 leading-none mb-2">{n}</div>
      <div className="font-heading font-bold text-base mb-1">{title}</div>
      <div className="text-sm text-slate-600 leading-relaxed">{body}</div>
    </div>
  );
}
function StatChip({ label, value }) {
  return (
    <div className="border-2 border-slate-900 rounded-full px-4 py-2 bg-white flex items-baseline gap-2">
      <span className="font-heading font-bold text-lg">{value}</span>
      <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</span>
    </div>
  );
}

// 7 — BRAINS
function Brains() {
  const strip = [...BRAIN_STRIP, ...BRAIN_STRIP]; // marquee dup
  return (
    <div className="w-full h-full flex items-center px-8 md:px-16" data-testid="slide-brains">
      <div className="max-w-6xl mx-auto w-full">
        <SectionEyebrow>06 · The brain catalog</SectionEyebrow>
        <h2 className="font-heading text-4xl md:text-6xl font-bold leading-none mb-4">
          12 brains. One interface.
        </h2>
        <p className="text-lg text-slate-600 mb-10 max-w-2xl">
          OpenRouter by default. Bring your own OpenAI / Anthropic / Google keys to bypass the middleman and cut cost 30-70%.
        </p>

        <div className="overflow-hidden border-y-2 border-slate-900 py-5 bg-white">
          <div className="marquee flex gap-3 whitespace-nowrap" style={{ width: "200%" }}>
            {strip.map((b, i) => (
              <div key={i} className="brut-card px-4 py-2 bg-white flex items-center gap-2 shrink-0">
                <span
                  className="w-2.5 h-2.5 rounded-full border border-slate-900"
                  style={{ backgroundColor: b.color }}
                />
                <span className="font-heading font-bold">{b.name}</span>
                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{b.accent}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 grid md:grid-cols-3 gap-4 stagger">
          <FeatureCol icon={Layers} title="Unified interface"
            body="One `chat_stream(brain_id, msgs)` under the hood. Never leak provider quirks up-stack." />
          <FeatureCol icon={DollarSign} title="Direct-key savings"
            body="Bring your own OpenAI/Anthropic/Google keys. Encrypted at rest. Provider-native rate + cost." />
          <FeatureCol icon={Zap} title="Auto Brain routing"
            body="Rule-based today. Learned from your history tomorrow." />
        </div>
      </div>
    </div>
  );
}

function FeatureCol({ icon: Icon, title, body }) {
  return (
    <div>
      <div className="w-10 h-10 rounded-xl border-2 border-slate-900 bg-white flex items-center justify-center mb-2">
        <Icon size={18} strokeWidth={2.5} />
      </div>
      <div className="font-heading font-bold text-lg mb-1">{title}</div>
      <div className="text-sm text-slate-600 leading-relaxed">{body}</div>
    </div>
  );
}

// 8 — PRODUCT HIGHLIGHTS
function ProductHighlights() {
  return (
    <div className="w-full h-full flex items-center px-8 md:px-16" data-testid="slide-highlights">
      <div className="max-w-6xl mx-auto w-full">
        <SectionEyebrow>07 · What's shipped</SectionEyebrow>
        <h2 className="font-heading text-4xl md:text-6xl font-bold leading-none mb-10">
          Not a demo. A product.
        </h2>

        <div className="grid md:grid-cols-2 gap-4 stagger">
          <Highlight
            emoji="⚔️" title="Debate Mode"
            body="Multi-worker parallel SSE with a structured judge verdict. Every debate auto-saved & shareable."
          />
          <Highlight
            emoji="🎨" title="Visual Workflow Canvas"
            body="10 node types on a React-Flow DAG: Worker · Debate · Router · Reviewer · Human-Approval · Tool · Knowledge. Parallel exec + budget guardrails."
          />
          <Highlight
            emoji="🧪" title="Model Studio + Marketplace"
            body="Compose personas, import from Hugging Face, plug in any OpenAI-compatible endpoint. Fork, star, grade, trend."
          />
          <Highlight
            emoji="🔥" title="Fine-Tune Bridge"
            body="Send LoRA jobs to Fireworks or Together AI, live cost estimator, auto-register outputs as your own Custom Brains."
          />
          <Highlight
            emoji="💬" title="Slack Slash-Commands"
            body="/aimedley <slug> runs a full workflow from Slack. HMAC-verified webhooks, block-kit responses back in channel."
          />
          <Highlight
            emoji="🔗" title="Public Share Links"
            body="Workers, debates, workflows, model cards — every artifact is a snapshot URL. Every share = a signup."
          />
        </div>
      </div>
    </div>
  );
}

function Highlight({ emoji, title, body }) {
  return (
    <div className="brut-card p-5 bg-white flex gap-4 items-start">
      <div className="text-4xl leading-none shrink-0">{emoji}</div>
      <div>
        <div className="font-heading font-bold text-lg mb-1">{title}</div>
        <div className="text-sm text-slate-600 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

// 9 — MARKET
function Market() {
  return (
    <div className="w-full h-full flex items-center px-8 md:px-16" data-testid="slide-market">
      <div className="max-w-6xl mx-auto w-full">
        <SectionEyebrow>08 · Market</SectionEyebrow>
        <h2 className="font-heading text-4xl md:text-6xl font-bold leading-none mb-4">
          The AI budget is exploding.<br /> Coordination eats the majority.
        </h2>
        <p className="text-slate-600 mb-10 max-w-3xl text-lg">
          Frontier labs sell the raw brains. Enterprises are already paying $61B/year on generative AI
          <span className="text-slate-400"> (IDC 2024)</span> — but 70%+ of that goes to <b>orchestration, seats, and integration</b>, not the models themselves.
          That's the layer we're building for.
        </p>

        <div className="grid md:grid-cols-3 gap-4 mb-10 stagger">
          <MarketRing label="TAM 2027" value="$407B" body="Global generative-AI enterprise spend (Bloomberg Intelligence). Doubles every 18 months." fill="#3B82F6" />
          <MarketRing label="SAM" value="$120B" body="Knowledge-worker coordination + workforce orchestration seats — the part above the raw model API." fill="#F59E0B" />
          <MarketRing label="Beachhead" value="200M+" body="Power users already juggling 2+ frontier + open models today (ChatGPT, Claude, Gemini, DeepSeek, Llama-hosted)." fill="#10B981" />
        </div>

        <div className="text-lg text-slate-600 max-w-3xl">
          Every share, fork, and Slack /aimedley call is <b>zero-CAC distribution</b>. The workforce comes with the network.
        </div>
      </div>
    </div>
  );
}

function MarketRing({ label, value, body, fill }) {
  return (
    <div className="brut-card p-6 bg-white">
      <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">{label}</div>
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-12 h-12 rounded-full border-2 border-slate-900"
          style={{ backgroundColor: fill + "44" }}
        />
        <div className="font-heading font-bold text-4xl leading-none">{value}</div>
      </div>
      <div className="text-sm text-slate-600 leading-relaxed">{body}</div>
    </div>
  );
}

// 10 — COMPETITION
function Competition() {
  return (
    <div className="w-full h-full flex items-center px-8 md:px-16" data-testid="slide-competition">
      <div className="max-w-6xl mx-auto w-full">
        <SectionEyebrow>09 · Competition</SectionEyebrow>
        <h2 className="font-heading text-4xl md:text-6xl font-bold leading-none mb-8">
          Nobody owns "portable AI colleagues".
        </h2>

        <div className="brut-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="text-left px-4 py-3 font-heading">Player</th>
                <th className="text-center px-3 py-3 font-heading">Multi-brain</th>
                <th className="text-center px-3 py-3 font-heading">Persistent persona</th>
                <th className="text-center px-3 py-3 font-heading">Teams</th>
                <th className="text-center px-3 py-3 font-heading">Debate</th>
                <th className="text-center px-3 py-3 font-heading">Public shares</th>
              </tr>
            </thead>
            <tbody>
              <Row name="ChatGPT + GPTs" cells={[false, "yes", false, false, false]} />
              <Row name="Claude Projects" cells={[false, "yes", false, false, false]} />
              <Row name="Poe" cells={["yes", false, false, false, "some"]} />
              <Row name="Character.ai" cells={[false, "yes", false, false, "yes"]} />
              <Row name="Perplexity" cells={["some", false, false, false, false]} />
              <Row name="CrewAI / LangGraph" cells={["yes", "yes", "yes", false, false]} highlight={false} devFirst />
              <Row name="AiMedley" cells={["yes", "yes", "yes", "yes", "yes"]} highlight />
            </tbody>
          </table>
        </div>

        <div className="mt-6 text-sm text-slate-600 max-w-3xl">
          <b className="text-slate-900">The uncontested wedge</b>: consumer-grade UX + multi-vendor brains + team + debate + zero-friction sharing.
          Dev frameworks are code. AiMedley is a product a marketing lead can use on Monday morning.
        </div>
      </div>
    </div>
  );
}

function Row({ name, cells, highlight, devFirst }) {
  return (
    <tr className={`border-t-2 border-slate-900 ${highlight ? "bg-amber-100" : "bg-white"}`}>
      <td className="px-4 py-3 font-heading font-bold">
        {name}
        {devFirst && <span className="ml-2 text-[9px] uppercase tracking-widest text-slate-500 border border-slate-500 rounded-full px-1.5 py-0.5">Dev-first</span>}
      </td>
      {cells.map((c, i) => (
        <td key={i} className="text-center px-3 py-3">
          {c === "yes" ? <span className="inline-block w-6 h-6 leading-6 rounded-full bg-emerald-500 text-white font-bold">✓</span> :
           c === "some" ? <span className="text-slate-500 text-xs uppercase tracking-widest font-bold">partial</span> :
           <span className="text-slate-300">—</span>}
        </td>
      ))}
    </tr>
  );
}

// 11 — BUSINESS MODEL
function BusinessModel() {
  return (
    <div className="w-full h-full flex items-center px-8 md:px-16" data-testid="slide-business">
      <div className="max-w-6xl mx-auto w-full">
        <SectionEyebrow>10 · Business model</SectionEyebrow>
        <h2 className="font-heading text-4xl md:text-6xl font-bold leading-none mb-10">
          Free viral bottom.<br />Team & Enterprise on top.
        </h2>

        <div className="grid md:grid-cols-4 gap-4 stagger">
          <Tier name="Free" price="$0" line="Try the whole thing" perks={["100k tokens / mo", "3 workers", "Public shares", "OpenRouter-powered"]} />
          <Tier name="Pro" price="$15" period="/mo" line="Power user" perks={["5M tokens / mo", "Unlimited workers", "Community publish", "Direct provider keys"]} highlight />
          <Tier name="Team" price="$40" period="/user/mo" line="Small teams" perks={["Shared budgets", "Team analytics", "Private community", "Priority streaming"]} />
          <Tier name="Enterprise" price="Call" line="Compliance + scale" perks={["SSO / SAML", "On-prem provider keys", "SOC 2 (Q3 roadmap)", "Custom brains"]} dark />
        </div>

        <div className="mt-8 text-sm text-slate-600 flex flex-wrap gap-6">
          <span><b className="text-slate-900">Blended ARPU target:</b> $12 / mo</span>
          <span><b className="text-slate-900">Free → Pro conv:</b> 4-6% (share-link driven)</span>
          <span><b className="text-slate-900">Gross margin:</b> 78% (direct-key adopters push it higher)</span>
        </div>
      </div>
    </div>
  );
}

function Tier({ name, price, period, line, perks, highlight, dark }) {
  return (
    <div className={`brut-card p-5 h-full flex flex-col ${
      highlight ? "bg-amber-100" : dark ? "bg-slate-900 text-white" : "bg-white"
    }`}>
      <div className={`text-xs uppercase tracking-widest font-bold mb-1 ${dark ? "text-amber-200" : "text-slate-500"}`}>{name}</div>
      <div className="flex items-baseline gap-1 mb-1">
        <span className="font-heading font-bold text-4xl leading-none">{price}</span>
        {period && <span className={`text-xs font-mono ${dark ? "text-slate-300" : "text-slate-500"}`}>{period}</span>}
      </div>
      <div className={`text-sm mb-4 ${dark ? "text-slate-300" : "text-slate-600"}`}>{line}</div>
      <ul className="space-y-1.5 text-sm">
        {perks.map((p, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check size={13} className={`mt-1 shrink-0 ${dark ? "text-amber-300" : "text-emerald-600"}`} strokeWidth={3} />
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// 12 — ROADMAP
function Roadmap() {
  return (
    <div className="w-full h-full flex items-center px-8 md:px-16" data-testid="slide-roadmap">
      <div className="max-w-6xl mx-auto w-full">
        <SectionEyebrow>11 · Roadmap</SectionEyebrow>
        <h2 className="font-heading text-4xl md:text-6xl font-bold leading-none mb-10">
          Shipped. Shipping. Next.
        </h2>

        <div className="space-y-3 stagger">
          <Milestone tag="✅ Shipped" tagColor="#10B981" title="Foundation — multi-brain workbench"
            items={["12-brain unified interface (GPT-5.1, Claude 4.6, Gemini 2.5, DeepSeek, Qwen, Kimi, Llama…)", "Chat · Compare · Research · Documents · Quick Agents", "JWT auth + Google OAuth · budgets · privacy hardened"]} />

          <Milestone tag="✅ Shipped" tagColor="#10B981" title="Agent Workforce + Debate"
            items={["Persistent workers · brain-swap · persona sliders · templates gallery", "Teams + broadcast · public share links · pins", "Debate Arena — multi-worker parallel SSE with structured verdict"]} />

          <Milestone tag="✅ Shipped" tagColor="#10B981" title="Visual Workflow Canvas"
            items={["React-Flow DAG editor · 10 node types (Start, Worker, Debate, Router, Reviewer, Human Approval, Tool, Knowledge, Condition, End)", "Parallel execution engine · budget guardrails · resume-from-approval", "TF-IDF vector knowledge search (scikit-learn)"]} />

          <Milestone tag="✅ Shipped" tagColor="#10B981" title="Model Studio + Community Marketplace"
            items={["Compose personas from any brain · Hugging Face import · Custom OpenAI-compatible endpoints (Fireworks, Together, Groq)", "Fork · star · grade suite · side-by-side diff · trending leaderboard", "Creator profiles · social graph (follow/star) · in-app notifications"]} />

          <Milestone tag="✅ Shipped" tagColor="#10B981" title="Fine-Tune Bridge + Slack"
            items={["Send supervised LoRA jobs to Fireworks / Together AI · auto-register outputs as Custom Brains", "Cost estimator · Together JSONL upload proxy · live status polling", "/aimedley slash command in Slack with signed webhook verification"]} />

          <Milestone tag="⚡ Shipping now" tagColor="#F59E0B" title="Live Training Logs + Block Kit"
            items={["SSE stream of loss/step logs from Fireworks & Together into the job detail page", "Slack responses rendered as Block Kit (cards, buttons, tables) — actionable, not just text"]} />

          <Milestone tag="◇ Next 90 days" tagColor="#94A3B8" title="Enterprise + intelligence"
            items={["Auto Brain v2 — learned routing across the fleet · MCP tool registry (SAP, Salesforce, Notion, GitHub, Jira)", "Bidirectional Slack/Discord bridges · SSO/SCIM (Okta, Azure AD, Workspace) · real-time collab (Yjs)", "Mobile companion app · advanced analytics · SOC 2"]} />
        </div>
      </div>
    </div>
  );
}

function Milestone({ tag, tagColor, title, items }) {
  return (
    <div className="brut-card p-4 bg-white flex gap-4 items-start">
      <span
        className="px-3 py-1 rounded-full border-2 border-slate-900 font-heading font-bold text-xs uppercase tracking-widest whitespace-nowrap shrink-0"
        style={{ backgroundColor: tagColor + "33", color: tagColor === "#94A3B8" ? "#475569" : "#0F172A" }}
      >
        {tag}
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-lg mb-1">{title}</div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-slate-600">
          {items.map((it, i) => (
            <span key={i}>{i > 0 && <span className="text-slate-300 mr-2">·</span>}{it}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// 13 — SURFACE AREA / PRODUCT DEPTH
function TractionMetrics() {
  return (
    <div className="w-full h-full flex items-center px-8 md:px-16" data-testid="slide-traction">
      <div className="max-w-6xl mx-auto w-full">
        <SectionEyebrow>12 · Surface area</SectionEyebrow>
        <h2 className="font-heading text-4xl md:text-6xl font-bold leading-none mb-6">
          A workbench, not a wrapper.
        </h2>
        <p className="text-lg text-slate-600 mb-10 max-w-3xl">
          Most "AI apps" ship one chat box in front of one model. AiMedley ships nine coordinated surfaces on top of every major brain — plus a marketplace and a fine-tune bridge to add your own.
        </p>

        <div className="grid md:grid-cols-4 gap-4 mb-8 stagger">
          <BigStat value="12+" label="Frontier + open brains" />
          <BigStat value="9" label="Product surfaces" />
          <BigStat value="10" label="Workflow node types" />
          <BigStat value="∞" label="Custom brains you can plug in" />
        </div>

        <div className="brut-card p-6 bg-white">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3">Every surface a wrapper never gives you</div>
          <VelocityBars />
        </div>
      </div>
    </div>
  );
}

function BigStat({ value, label }) {
  return (
    <div className="brut-card p-5 bg-white">
      <div className="font-heading font-bold text-4xl md:text-5xl leading-none mb-2">{value}</div>
      <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{label}</div>
    </div>
  );
}

function VelocityBars() {
  const surfaces = [
    { label: "Chat",       h: 55, color: "#3B82F6" },
    { label: "Compare",    h: 65, color: "#F59E0B" },
    { label: "Research",   h: 60, color: "#10B981" },
    { label: "Documents",  h: 55, color: "#8B5CF6" },
    { label: "Debate",     h: 85, color: "#EC4899" },
    { label: "Workforce",  h: 90, color: "#EF4444" },
    { label: "Workflows",  h: 95, color: "#F97316" },
    { label: "Studio",     h: 100, color: "#0F172A" },
    { label: "Slack",      h: 70, color: "#6366F1" },
  ];
  return (
    <div className="flex items-end gap-3 h-40" data-testid="deck-surface-bars">
      {surfaces.map((b) => (
        <div key={b.label} className="flex-1 flex flex-col items-center">
          <div className="w-full border-2 border-slate-900 rounded-t-lg transition-all"
               style={{ height: `${b.h}%`, backgroundColor: b.color }} />
          <div className="mt-2 text-[10px] uppercase tracking-widest font-bold text-slate-500">{b.label}</div>
        </div>
      ))}
    </div>
  );
}

// 14 — WHY WE WIN
function Ask() {
  return (
    <div className="w-full h-full flex items-center px-8 md:px-16" data-testid="slide-ask">
      <div className="max-w-6xl mx-auto w-full">
        <SectionEyebrow>13 · Why we win</SectionEyebrow>
        <h2 className="font-heading text-5xl md:text-7xl font-bold leading-none mb-6">
          The <span className="bg-amber-200 border-2 border-slate-900 px-3 inline-block -rotate-1">workforce layer</span>
          <br /> of AI is up for grabs.
        </h2>
        <p className="text-lg text-slate-600 mb-8 max-w-3xl">
          Frontier labs will keep making brains. Every enterprise will keep buying seats.
          But the layer between them — where personas persist, teams collaborate, and workflows run —
          is a wide-open moat. That's the layer we own.
        </p>

        <div className="grid md:grid-cols-3 gap-4 mb-8 stagger">
          <AskCard tag="Market" value="$407B by 2027" body="Generative AI enterprise spend, per Bloomberg Intelligence. Coordination + orchestration is a majority of that budget — and it's fragmenting away from single-vendor chat apps." />
          <AskCard tag="Wedge" value="Persona × brain" body="Every worker survives the next model launch. Users bring their team of colleagues along — Llama today, GPT-6 tomorrow, DeepSeek next quarter. Zero switching cost." />
          <AskCard tag="Moat" value="Marketplace × workflows" body="Forkable models, grade suites, viral trending, Slack-bridged workflows. Every share creates a signup. Every fork creates a derivative. Network effects compound." />
        </div>

        <div className="brut-card p-6 bg-slate-900 text-white flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest font-bold text-amber-200 mb-1">Try every surface — no signup wall on shares</div>
            <div className="font-heading font-bold text-xl md:text-2xl">A workforce that keeps working when the models change.</div>
          </div>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 bg-amber-300 text-slate-900 border-2 border-white px-5 py-3 rounded-full font-heading font-bold hover:bg-white transition-colors shrink-0"
            data-testid="deck-cta"
          >
            <Rocket size={16} strokeWidth={2.5} /> Start your workforce
            <ArrowRight size={16} strokeWidth={2.5} />
          </Link>
        </div>

        <div className="mt-6 text-center">
          <span className="inline-flex items-center gap-2 text-sm text-slate-500 font-mono">
            <Star size={12} strokeWidth={3} className="text-amber-500" />
            "Multi-brain is the assumption. Persona is the product."
            <Star size={12} strokeWidth={3} className="text-amber-500" />
          </span>
        </div>
      </div>
    </div>
  );
}

function AskCard({ tag, value, body }) {
  return (
    <div className="brut-card p-5 bg-white">
      <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">{tag}</div>
      <div className="font-heading font-bold text-4xl mb-2 leading-none">{value}</div>
      <div className="text-sm text-slate-600 leading-relaxed">{body}</div>
    </div>
  );
}

/* ============================================================
   SHARED BITS
   ============================================================ */
function SectionEyebrow({ children }) {
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border-2 border-slate-900 font-heading font-bold text-xs uppercase tracking-widest mb-6">
      {children}
    </div>
  );
}
